import { useState, useRef, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { useSettings } from "@/engine/settingsStore";

const BASE_VOLUME = 0.15;
const FADE_DURATION = 1500; // ms

// ── Per-track loudness normalisation (perceived / K-weighted) ───────
// The source mp3s were mastered at wildly different levels. Each value
// below is a multiplier on BASE_VOLUME that pulls every track to a
// common PERCEIVED loudness, measured to the ITU-R BS.1770 / EBU R128
// standard: K-weighting (a filter that models the ear's frequency
// sensitivity) is applied before integrating, so a bass-heavy track and
// a bright one are compared the way a listener actually hears them — not
// by flat RMS energy. Raw integrated loudness spans ~-7.9 LUFS (loudest)
// to ~-25.5 LUFS (quietest): a ~17.6 LU range.
//
// Gains normalise every track to the ~-16.6 LUFS median, so the median
// track keeps gain 1.0 (overall level unchanged — this only EQUALISES);
// louder tracks are attenuated, quieter ones lifted. Post-normalisation
// every track sits within ~0.1 LU of the median — flat to the ear (the
// ear needs ~1 LU to register a difference).
//
// This SUPERSEDES the earlier flat-RMS pass, which weighted every
// frequency equally and so mismeasured perceived loudness: the bass-
// heavy Carolingian (2.51→1.74) and Dutch-Golden-Age (2.88→2.34) tracks
// were rated quiet by RMS and over-boosted; K-weighting corrects them.
//
// effectiveBase = BASE_VOLUME × gain peaks at ~0.42 (Iceland, 2.80) —
// well under the HTMLAudioElement volume ceiling of 1.0; no track clips.
// Keyed by the served /audio path; sorted quietest-playback → loudest.
//
// ▶ RE-MEASURE: run `npm run audio:normalize`. The script measures
//   every `public/audio/*.mp3` headlessly via ffmpeg's `ebur128` filter
//   (same ITU-R BS.1770 K-weighting), normalises every track to the
//   cohort's median LUFS, and rewrites the AUTO-GENERATED block below
//   in place. Also wired as `prebuild` / `predev` and cached by audio-
//   set hash, so unchanged runs are free. CI gate: `npm run audio:check`
//   fails if any mp3 lacks a TRACK_GAIN entry. If ffmpeg is missing the
//   script warns and exits 0 — the in-browser fallback at
//   `tools/measure-loudness.html` still works.
// AUTO-GENERATED by `npm run audio:normalize` — do not hand-edit.
// Median target: -15.60 LUFS. Measured 2026-10-21 via ffmpeg ebur128.
const TRACK_GAIN: Record<string, number> = {
  "/audio/world-war-two-europe-stiller-held-ambient.mp3": 0.33,
  "/audio/serbian-uprisings-ambient.mp3": 0.36,
  "/audio/napoleonic-wars-grognard-ambient.mp3": 0.39,
  "/audio/siege-of-constantinople-ambient.mp3": 0.43,
  "/audio/russian-revolution-ambient.mp3": 0.49,
  "/audio/great-northern-war-swedish-ambient.mp3": 0.51,
  "/audio/fall-of-communism-ambient.mp3": 0.52,
  "/audio/hungarian-revolution-1848-ambient.mp3": 0.52,
  "/audio/magyar-raids-ambient.mp3": 0.61,
  "/audio/russian-revolution-white-ambient.mp3": 0.62,
  "/audio/thirty-years-war-soldier-ambient.mp3": 0.62,
  "/audio/world-war-one-poilu-ambient.mp3": 0.63,
  "/audio/fall-of-granada-ambient.mp3": 0.64,
  "/audio/prague-spring-1968-ambient.mp3": 0.65,
  "/audio/wars-of-the-roses-ambient.mp3": 0.65,
  "/audio/great-northern-war-ambient.mp3": 0.67,
  "/audio/irish-independence-ambient.mp3": 0.68,
  "/audio/russian-revolution-red-ambient.mp3": 0.70,
  "/audio/german-unification-ambient.mp3": 0.71,
  "/audio/spanish-civil-war-ending.mp3": 0.71,
  "/audio/livonian-crusade-ambient.mp3": 0.75,
  "/audio/christianization-scandinavia-ambient.mp3": 0.76,
  "/audio/viking-dublin-ambient.mp3": 0.79,
  "/audio/english-civil-wars-ambient.mp3": 0.80,
  "/audio/greek-independence-ambient.mp3": 0.83,
  "/audio/kalmar-union-ambient.mp3": 0.83,
  "/audio/world-war-one-bef-ambient.mp3": 0.86,
  "/audio/black-death-ambient.mp3": 0.88,
  "/audio/hussite-wars-ambient.mp3": 0.88,
  "/audio/map-ambient.mp3": 0.89,
  "/audio/napoleonic-wars-ambient.mp3": 0.89,
  "/audio/carolingian-empire-ambient.mp3": 0.90,
  "/audio/eighty-years-war-ambient.mp3": 0.91,
  "/audio/hungarian-revolution-1956-ambient.mp3": 0.91,
  "/audio/the-anarchy-ambient.mp3": 1.00,
  "/audio/second-republic-ambient.mp3": 1.01,
  "/audio/two-germanies-ambient.mp3": 1.01,
  "/audio/spanish-civil-war-ambient.mp3": 1.07,
  "/audio/world-war-one-ambient.mp3": 1.08,
  "/audio/napoleonic-wars-bluejacket-ambient.mp3": 1.10,
  "/audio/great-northern-war-russian-ambient.mp3": 1.11,
  "/audio/reconquista-ambient.mp3": 1.11,
  "/audio/italian-city-states-ambient.mp3": 1.12,
  "/audio/albigensian-crusade-ambient.mp3": 1.14,
  "/audio/protestant-reformation-ambient.mp3": 1.16,
  "/audio/norman-conquest-ambient.mp3": 1.16,
  "/audio/world-war-two-europe-occupe-ambient.mp3": 1.17,
  "/audio/napoleonic-wars-prussian-ambient.mp3": 1.27,
  "/audio/mongol-invasions-ambient.mp3": 1.35,
  "/audio/french-revolution-ambient.mp3": 1.41,
  "/audio/italian-unification-ambient.mp3": 1.43,
  "/audio/hundred-years-war-ambient.mp3": 1.48,
  "/audio/world-war-one-prussian-ambient.mp3": 1.50,
  "/audio/burgundian-netherlands-ambient.mp3": 1.57,
  "/audio/peninsular-war-ambient.mp3": 1.57,
  "/audio/medici-florence-ambient.mp3": 1.60,
  "/audio/investiture-controversy-ambient.mp3": 1.64,
  "/audio/thirty-years-war-ambient.mp3": 1.68,
  "/audio/belgian-revolution-ambient.mp3": 1.74,
  "/audio/ottoman-expansion-ambient.mp3": 1.76,
  "/audio/first-voyages-west-ambient.mp3": 1.95,
  "/audio/world-war-two-europe-witness-ambient.mp3": 2.00,
  "/audio/the-deluge-ambient.mp3": 2.07,
  "/audio/italian-wars-ambient.mp3": 2.16,
  "/audio/iberian-union-ambient.mp3": 2.19,
  "/audio/dutch-golden-age-ambient.mp3": 2.37,
  "/audio/world-war-two-europe-blitz-ambient.mp3": 2.79,
  "/audio/settlement-of-iceland-ambient.mp3": 3.09,
  "/audio/world-war-two-europe-ambient.mp3": 3.63,
};

// Resolved per-track playback volume — the loudness-normalised base
// level BEFORE the user's master-volume multiplier. This is the SINGLE
// source of truth shared by the chronicle ambient (AmbientMusicProvider
// below) AND the home-map track (src/pages/Index.tsx), so every track in
// the game is perceptually level-matched to the same median. A null or
// unrecognised src falls back to BASE_VOLUME (gain 1.0).
export function trackVolume(src: string | null | undefined): number {
  return BASE_VOLUME * (src ? (TRACK_GAIN[src] ?? 1) : 1);
}

function fadeAudio(audio: HTMLAudioElement, from: number, to: number, duration: number): Promise<void> {
  return new Promise((resolve) => {
    const steps = 30;
    const stepTime = duration / steps;
    const diff = to - from;
    let step = 0;
    const interval = setInterval(() => {
      step++;
      audio.volume = Math.max(0, Math.min(1, from + diff * (step / steps)));
      if (step >= steps) {
        clearInterval(interval);
        audio.volume = Math.max(0, Math.min(1, to));
        resolve();
      }
    }, stepTime);
  });
}

// ═══════════════════════════════════════════════════════════════════
// MODULE-LEVEL AUDIO SINGLETON — lives outside React's lifecycle.
// ═══════════════════════════════════════════════════════════════════
//
// The audio element lives at module scope; it persists for the
// entire SPA session. React only owns the controls (mute, volume,
// switchTrack), not the element itself. This makes restart-on-
// navigation literally impossible: there is no point in the React
// render tree that owns the audio to remount.
//
// Cross-src navigation (e.g. Home → Chronicle A → Home → Chronicle B)
// is handled by `setAudioSrc()` — when called with a NEW src, the
// existing singleton is paused, its src is swapped, currentTime
// resets to 0, and play resumes. When called with the SAME src,
// the singleton keeps playing from its current position. When
// called with `null` (home route, no active chronicle), the
// singleton is paused but not torn down.
let moduleAudio: HTMLAudioElement | null = null;
let moduleAudioSrc: string | null = null;
let moduleAudioGesturePlayed = false;
let moduleFadeInProgress = false;

function ensureModuleAudio(): HTMLAudioElement {
  if (moduleAudio) return moduleAudio;
  const a = new Audio();
  a.loop = true;
  a.volume = 0;
  a.preload = "auto";
  moduleAudio = a;
  return a;
}

function setAudioSrc(newSrc: string | null, startOffset: number): boolean {
  // Returns true if the src actually changed (caller should retry play).
  const audio = ensureModuleAudio();
  if (newSrc === null) {
    if (moduleAudioSrc !== null) {
      try { audio.pause(); } catch {}
    }
    moduleAudioSrc = null;
    return false;
  }
  if (moduleAudioSrc === newSrc) {
    // Same track — do NOT touch currentTime, volume, or anything else.
    return false;
  }
  // Real track change. Stop the old, swap the src.
  try { audio.pause(); } catch {}
  audio.src = newSrc;
  audio.volume = 0;
  moduleAudioSrc = newSrc;
  moduleAudioGesturePlayed = false;
  const seek = () => {
    if (startOffset > 0) {
      try { audio.currentTime = startOffset; } catch {}
    }
  };
  if (audio.readyState >= 1) {
    seek();
  } else {
    const onLoaded = () => { seek(); audio.removeEventListener("loadedmetadata", onLoaded); };
    audio.addEventListener("loadedmetadata", onLoaded);
  }
  return true;
}

interface AmbientMusicContextValue {
  muted: boolean;
  toggle: () => void;
  switchTrack: (newSrc: string) => void;
  resetTrack: () => void;
}

const AmbientMusicContext = createContext<AmbientMusicContextValue>({
  muted: false,
  toggle: () => {},
  switchTrack: () => {},
  resetTrack: () => {},
});

export function useAmbientMusic() {
  return useContext(AmbientMusicContext);
}

// ════════════════════════════════════════════════════════════════
// IMPORTANT — DO NOT MOUNT THIS PROVIDER INSIDE A ROUTE COMPONENT.
// ════════════════════════════════════════════════════════════════
//
// As of 2026-05 the AmbientMusicProvider lives at the App level
// (src/App.tsx, ABOVE the <Routes>), wrapped by RouterAwareAmbientMusic
// which derives `src` from `useLocation()`. This means the provider
// stays mounted across EVERY route change in the app — Home →
// /chronicle/:id → /chronicle/:id/protagonist → /chronicle/:id/play.
//
// Independently, the audio ELEMENT itself lives at module scope
// (see ensureModuleAudio / setAudioSrc above) — so even if a future
// edit accidentally regressed the Provider's placement, the audio
// would still survive. The Provider is now a thin React surface
// over a non-React audio singleton.
//
// `src` is `string | undefined`. When `undefined` (e.g. on the home
// route where no chronicle is active) the audio is paused but the
// singleton persists. Switching to a real src resumes / swaps the
// track; switching back to undefined pauses without tearing down.
//
export function AmbientMusicProvider({
  src,
  startOffset = 0,
  children,
}: {
  src: string | undefined;
  startOffset?: number;
  children: React.ReactNode;
}) {
  const { settings, update } = useSettings();
  const [activeSrc, setActiveSrc] = useState(src);

  // Mirror musicMuted into a ref so the visibility / autoplay-retry
  // handlers below — which are registered ONCE per mount and would
  // otherwise close over a stale `settings.musicMuted` from the first
  // render — always read the latest mute state. Without this ref,
  // toggling mute, switching tabs, and returning replays the music
  // (the visibility handler still thought "not muted").
  const mutedRef = useRef(settings.musicMuted);
  useEffect(() => { mutedRef.current = settings.musicMuted; }, [settings.musicMuted]);

  // Mirror update + the live mute state into a ref so `toggle` can
  // stay stable across renders (see context-value memo below).
  const updateRef = useRef(update);
  useEffect(() => { updateRef.current = update; }, [update]);
  const musicMutedRefForToggle = useRef(settings.musicMuted);
  useEffect(() => { musicMutedRefForToggle.current = settings.musicMuted; }, [settings.musicMuted]);

  // Keep activeSrc in sync when navigating between chronicles
  useEffect(() => {
    setActiveSrc(src);
  }, [src]);

  // Effective offset only applies to the chronicle's own track (not switchTrack overrides)
  const effectiveOffset = activeSrc === src ? startOffset : 0;

  // Per-track loudness normalisation via the shared trackVolume() helper
  // (see TRACK_GAIN above): every track is pulled to the same PERCEIVED
  // level instead of a flat base. Unknown / null src → gain 1.0.
  const effectiveBase = trackVolume(activeSrc);

  // Sync the module-level audio singleton with the React activeSrc.
  // Critically, when `activeSrc` is unchanged, setAudioSrc is a no-op
  // — it does NOT touch currentTime, volume, or pause/play state.
  // This is what makes navigation between the three chronicle screens
  // (Detail / ProtagonistIntro / Play) NEVER restart the music: the
  // src string is the same; setAudioSrc returns immediately; the
  // singleton plays uninterrupted.
  useEffect(() => {
    if (!activeSrc) {
      setAudioSrc(null, 0);
      return;
    }
    const targetVol = effectiveBase * (settings.masterVolume / 100);
    const changed = setAudioSrc(activeSrc, effectiveOffset);
    const audio = moduleAudio!;

    const tryPlay = () => {
      if (mutedRef.current) return;
      if (document.visibilityState === "hidden") return;
      if (moduleAudioGesturePlayed && !audio.paused) return;
      audio.play().then(() => {
        moduleAudioGesturePlayed = true;
        document.removeEventListener("click", tryPlay);
        document.removeEventListener("keydown", tryPlay);
        document.removeEventListener("touchstart", tryPlay);
        // Fade in ONLY on a real src change. Same-src navigations
        // never trigger a fade — they would sound like a restart.
        if (changed && !moduleFadeInProgress) {
          moduleFadeInProgress = true;
          fadeAudio(audio, 0, targetVol, FADE_DURATION).finally(() => {
            moduleFadeInProgress = false;
          });
        } else if (!changed) {
          audio.volume = targetVol;
        }
      }).catch(() => {});
    };

    // Try immediately; if blocked by autoplay policy, wait for a user gesture.
    tryPlay();
    document.addEventListener("click", tryPlay);
    document.addEventListener("keydown", tryPlay);
    document.addEventListener("touchstart", tryPlay);

    // Visibility handling — when the page is backgrounded pause the
    // singleton; resume on return ONLY if not muted. Reads the live
    // mute state via the ref to avoid the stale-closure bug.
    const onVisibility = () => {
      if (!moduleAudio) return;
      if (document.visibilityState === "hidden") {
        moduleAudio.pause();
      } else if (!mutedRef.current) {
        moduleAudio.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Re-apply offset on loop wrap (only when wrapping back near 0).
    const onTimeUpdate = () => {
      if (effectiveOffset > 0 && audio.currentTime < effectiveOffset - 0.5) {
        try { audio.currentTime = effectiveOffset; } catch {}
      }
    };
    if (effectiveOffset > 0) audio.addEventListener("timeupdate", onTimeUpdate);

    return () => {
      document.removeEventListener("click", tryPlay);
      document.removeEventListener("keydown", tryPlay);
      document.removeEventListener("touchstart", tryPlay);
      document.removeEventListener("visibilitychange", onVisibility);
      if (effectiveOffset > 0) audio.removeEventListener("timeupdate", onTimeUpdate);
      // Do NOT pause / clear src here. The audio singleton lives
      // beyond React's lifecycle — only setAudioSrc(null, …) on a
      // genuine track change tears down playback.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSrc, effectiveOffset]);

  // Sync volume — operates on the singleton directly.
  useEffect(() => {
    if (!moduleAudio || moduleFadeInProgress) return;
    moduleAudio.volume = effectiveBase * (settings.masterVolume / 100);
  }, [settings.masterVolume, effectiveBase]);

  // Sync mute / resume
  useEffect(() => {
    if (!moduleAudio) return;
    if (settings.musicMuted) {
      moduleAudio.muted = true;
    } else {
      moduleAudio.muted = false;
      if (moduleAudio.paused && moduleAudioSrc) {
        moduleAudio.play().catch(() => {});
      }
    }
  }, [settings.musicMuted]);

  // ALL three callbacks are stable across renders. `toggle` reads live
  // state via refs so it doesn't need musicMuted in its dep list.
  // This matters because the Provider value object below — if recreated
  // every render — would force every component subscribed to
  // useAmbientMusic() to re-render on every route change and every
  // settings tick, which adds up across the chronicle screens (the
  // hover tooltip, the toggle, ChronicleDetail's resetTrack effect,
  // ProtagonistIntro's resetTrack effect, Play's switchTrack effect
  // chain — all consumers).
  const toggle = useCallback(() => {
    if (moduleAudio?.paused && moduleAudioSrc) {
      moduleAudio.play().catch(() => {});
    }
    updateRef.current({ musicMuted: !musicMutedRefForToggle.current });
  }, []);

  const switchTrack = useCallback((newSrc: string) => {
    setActiveSrc((cur) => (cur === newSrc ? cur : newSrc));
  }, []);

  const resetTrack = useCallback(() => {
    setActiveSrc((cur) => (cur === src ? cur : src));
  }, [src]);

  // Memoised so the context value is referentially stable when its
  // members are — preventing cascading re-renders of every
  // useAmbientMusic() consumer on each Provider render.
  const contextValue = useMemo<AmbientMusicContextValue>(
    () => ({ muted: settings.musicMuted, toggle, switchTrack, resetTrack }),
    [settings.musicMuted, toggle, switchTrack, resetTrack],
  );

  return (
    <AmbientMusicContext.Provider value={contextValue}>
      {children}
    </AmbientMusicContext.Provider>
  );
}

export default function AmbientMusicToggle() {
  const { muted, toggle } = useAmbientMusic();

  return (
    <button
      onClick={toggle}
      className="p-2 text-muted-foreground hover:text-foreground transition-colors"
      title={muted ? "Unmute music" : "Mute music"}
    >
      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
    </button>
  );
}
